import { useEffect, useState } from "react"
import { Link, router } from "@inertiajs/react"
import { Menu, X } from "lucide-react"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip"
import type { NavLink, NavSection } from "./navLinks"
import { useNavSections } from "./useNavSections"

// The only way around Atlas: a Menu button at the left of the top bar that
// drops every page down at once, over a dimmed page. A tinted first column
// holds Home and My Work with the smaller sections stacked under them; then
// one column each for Planning, Purchase and Push. Columns wrap onto more
// lines when the page is narrow (the Claude panel open beside it).
// Closes on a link click, a click on the dimmed page, Esc, or any navigation.
// M opens and closes it, except while typing in a box (then M types an "m").
export default function NavMenu() {
  const [open, setOpen] = useState(false)
  const { sections, active } = useNavSections()

  useEffect(() => router.on("navigate", () => setOpen(false)), [])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== "m" || e.metaKey || e.ctrlKey || e.altKey || e.repeat) return
      if (isTyping(e.target)) return
      e.preventDefault()
      setOpen((o) => !o)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false) }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open])

  const side = sections.filter((s) => !s.icon)
  const main = sections.filter((s) => s.icon)

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-keyshortcuts="M"
            className={`flex h-full flex-shrink-0 items-center gap-2 border-r border-border px-4 text-sm font-semibold transition-colors ${
              open ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted"
            }`}
          >
            {open ? <X size={16} strokeWidth={2.25} /> : <Menu size={16} strokeWidth={2.25} />}
            {open ? "Close" : "Menu"}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start">{open ? "Close menu (M)" : "Open menu (M)"}</TooltipContent>
      </Tooltip>

      {open && (
        <>
          {/* Dims the page below the top bar only; the bar stays clear so Close,
              the market filter and search look the same as before. */}
          <div className="absolute inset-x-0 top-full z-30 h-screen bg-foreground/25 animate-in fade-in duration-150" onClick={() => setOpen(false)} />
          <nav
            aria-label="Main"
            className="absolute inset-x-0 top-full z-40 flex max-h-[calc(100vh-80px)] flex-wrap overflow-y-auto border-y border-border bg-background shadow-lg animate-in fade-in slide-in-from-top-2 duration-150"
          >
            <div className="flex w-[250px] flex-shrink-0 flex-col gap-4 bg-muted/50 p-4">
              {side.map((s) => (
                <div key={s.heading || "start"} className="flex flex-col gap-0.5">
                  {s.heading && (
                    <div className="px-2.5 pb-1 text-[11px] font-semibold uppercase text-muted-foreground" style={{ letterSpacing: "1.2px" }}>
                      {s.heading}
                    </div>
                  )}
                  {s.links.map((l) => <MenuLink key={l.href} link={l} active={l.href === active} onPick={() => setOpen(false)} />)}
                </div>
              ))}
            </div>

            {main.map((s) => (
              <div key={s.heading} className="flex min-w-[220px] flex-1 flex-col gap-0.5 p-4">
                <SectionHeading section={s} />
                {s.links.map((l) => <MenuLink key={l.href} link={l} active={l.href === active} onPick={() => setOpen(false)} />)}
              </div>
            ))}
          </nav>
        </>
      )}
    </>
  )
}

// A box that takes typing: inputs, text areas, selects, rich-text editors.
function isTyping(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)
}

// Planning, Purchase, Push: icon in a tinted square, name, and what the part is for.
function SectionHeading({ section }: { section: NavSection }) {
  const Icon = section.icon!
  return (
    <div className="mb-2 flex items-start gap-2.5 border-b border-border px-2.5 pb-3">
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Icon size={17} strokeWidth={2.25} />
      </div>
      <div className="min-w-0">
        <div className="text-[15px] font-semibold leading-5 text-foreground">{section.heading}</div>
        <div className="text-xs leading-4 text-muted-foreground">{section.description}</div>
      </div>
    </div>
  )
}

function MenuLink({ link, active, onPick }: { link: NavLink; active: boolean; onPick: () => void }) {
  const Icon = link.icon
  return (
    <Link
      href={link.href}
      onClick={onPick}
      aria-current={active ? "page" : undefined}
      className={`group flex items-start gap-2.5 rounded-md px-2.5 py-2 transition-colors ${active ? "bg-primary/10" : "hover:bg-muted"}`}
    >
      {/* Same width as the section's icon square, so icons share a center
          line and labels start where the section name does. */}
      <span className="flex h-5 w-8 flex-shrink-0 items-center justify-center">
        {Icon && <Icon size={16} strokeWidth={2} className={active ? "text-primary" : "text-muted-foreground group-hover:text-foreground"} />}
      </span>
      <span className="min-w-0">
        <span className={`block text-sm leading-5 ${active ? "font-semibold text-primary" : "font-medium text-foreground"}`}>{link.label}</span>
        {link.description && <span className="block text-xs leading-4 text-muted-foreground">{link.description}</span>}
      </span>
    </Link>
  )
}
